import {useMessage} from '@coolcolduk/ui-mui';
import {useCallback} from 'react';
import {useLocation, useNavigate} from 'react-router';
import {QuestionStatus, User, UserPermission} from '@cp/model';
import useQuestionFilters from '../../../components/QuestionFilters/useQuestionFilters';
import {env} from '../../../config/env';
import useListExamModules from '../../../hooks/examModules/useListExamModules';
import {useCountQuestionsByStatus} from '../../../hooks/question/useCountQuestionsByStatus';

export default function useAdminQuestionReviewPage(user: User) {
  const navigate = useNavigate();
  const location = useLocation();
  const {fetchCountsByStatus, questionCounts, loading: countLoading} = useCountQuestionsByStatus();
  const {showMessage} = useMessage();
  const {examModules, loading: modulesLoading} = useListExamModules();

  const isAdmin = Boolean(user?.roles?.includes(UserPermission.ADMIN));
  const isReviewer = Boolean(user?.roles?.includes(UserPermission.REVIEWER)) && !isAdmin;

  const {filters, QuestionFiltersComponent} = useQuestionFilters({
    examModules,
    modulesLoading,
    moduleLabel: 'Module',
    disabled: false,
    statusAllowAll: !isReviewer || isAdmin,
    statusAvailableStatuses: isReviewer && !isAdmin ? [QuestionStatus.VALIDATED] : Object.values(QuestionStatus),
    statusDisabled: isReviewer && !isAdmin,
    statusMinWidth: 150,
    useUrlParams: true, // Enable URL params persistence
    defaultExpanded: true,
  });

  const handleNavigateToQuestionReview = useCallback(() => {
    if (!filters.moduleId) {
      showMessage('Please select a module', 3000, 'error');
      return;
    }

    // Navigate to question review page with filters in URL - the page will fetch the question automatically
    const currentSearch = location.search;
    // location.search already includes the '?' prefix, so we don't need to add it again
    const searchString = currentSearch || '';
    navigate(`/admin/questionReview/${env.questionReview.noQuestionIs}${searchString}`);
  }, [filters.moduleId, showMessage, navigate, location.search]);

  const handleCountQuestions = useCallback(async () => {
    if (!filters.moduleId) {
      showMessage('Please select a module', 3000, 'error');
      return;
    }

    try {
      await fetchCountsByStatus({
        moduleId: filters.moduleId,
        topics: filters.topic ? [filters.topic] : undefined,
        difficulties: filters.difficulty ? [filters.difficulty] : undefined,
      });
      showMessage('Question counts loaded successfully', 3000, 'success');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to count questions';
      showMessage(errorMessage, 3000, 'error');
    }
  }, [filters, fetchCountsByStatus, showMessage]);

  return {
    filters,
    QuestionFiltersComponent,
    handleNavigateToQuestionReview,
    handleCountQuestions,
    countLoading,
    questionCounts,
    isAdmin,
    isReviewer,
  };
}
